// Every tasting a person has left open, from wherever it is known, as one list: the runs on this
// device, the copies another device carried to the edge, and the flights and sessions the edge says
// they are in. One tasting appears once, however many of those know of it. Pure: the screens fetch,
// this decides what is shown and where a tap leads.
import type {
  FlightSummary,
  SessionSummary,
  TastingDraftItem,
  TastingRunSnapshot,
} from "@proef/contract";
import { settleDraft } from "@proef/domain";
import { isUnfinished, type TastingRun } from "./run";
import type { NoteDepth } from "../noteDraft";

/** Where a tap on an unfinished tasting leads, and what it takes to get there. */
export type ResumeTarget =
  | { readonly kind: "run"; readonly runId: string }
  | { readonly kind: "draft"; readonly snapshot: TastingRunSnapshot }
  | { readonly kind: "flight"; readonly code: string; readonly sessionId: string | null }
  | { readonly kind: "session"; readonly sessionId: string };

/** What closing a shared tasting from the list does: the host ends it for everyone, anyone else leaves it. A flight outside a session is closed as itself. */
export type CloseTarget =
  | { readonly kind: "end_session"; readonly sessionId: string }
  | { readonly kind: "leave_session"; readonly sessionId: string }
  | { readonly kind: "end_flight"; readonly code: string }
  | { readonly kind: "leave_flight"; readonly code: string };

export interface UnfinishedItem {
  /** Stable across fetches, so a list does not flicker. */
  readonly id: string;
  readonly title: string;
  /** Which wine is in front, 1-based; none for a session that has no flight yet. */
  readonly wineNo: number | undefined;
  readonly wineTotal: number;
  readonly depth: NoteDepth | undefined;
  readonly startedAt: string | undefined;
  /** Others are in it: a flight, a session, a tasting opened up to people. */
  readonly shared: boolean;
  /** Finished by the taster, but a note still waits for a connection. */
  readonly waiting: boolean;
  /** This copy came from another device. */
  readonly fromOtherDevice: boolean;
  /** Orders the list, newest first. */
  readonly recency: string;
  /** Only a tasting held as a copy can be thrown away from here; a flight or session is closed instead (see `closing`). */
  readonly discardable: boolean;
  /** How a flight or a session is closed from here; a copy held here is discarded instead. */
  readonly closing: CloseTarget | undefined;
  readonly target: ResumeTarget;
}

export interface UnfinishedInput {
  readonly runs: readonly TastingRun[];
  readonly drafts: readonly TastingDraftItem[];
  readonly flights: readonly FlightSummary[];
  /** Live sessions the person is in. */
  readonly sessions: readonly SessionSummary[];
}

function ofRun(run: TastingRun): UnfinishedItem {
  return {
    id: `run:${run.id}`,
    title: run.title,
    wineNo: Math.min(run.current + 1, Math.max(run.wines.length, 1)),
    wineTotal: run.wines.length,
    depth: run.depth,
    startedAt: run.startedAt,
    shared: run.flightCode !== undefined,
    waiting: run.finished || (run.wines.length > 0 && run.wines.every((w) => w.saved)),
    fromOtherDevice: false,
    recency: run.updatedAt,
    discardable: true,
    closing: undefined,
    target: { kind: "run", runId: run.id },
  };
}

function ofDraft(draft: TastingDraftItem): UnfinishedItem {
  const run = draft.snapshot;
  return {
    ...ofRun(run),
    id: `run:${run.id}`,
    fromOtherDevice: true,
    recency: draft.updatedAt,
    target: { kind: "draft", snapshot: run },
  };
}

const closeOf = (sessionId: string, isHost: boolean): CloseTarget => ({
  kind: isHost ? "end_session" : "leave_session",
  sessionId,
});

function ofFlight(flight: FlightSummary, sessions: readonly SessionSummary[]): UnfinishedItem {
  // The session's own host flag decides; the flight's stands in when the list did not name the session.
  const isHost = (sessionId: string): boolean =>
    sessions.find((session) => session.id === sessionId)?.isHost ?? flight.isHost;
  return {
    id: `flight:${flight.code}`,
    title: flight.title,
    wineNo: Math.min(flight.currentIndex + 1, Math.max(flight.wineCount, 1)),
    wineTotal: flight.wineCount,
    depth: undefined,
    startedAt: flight.createdAt,
    shared: true,
    waiting: false,
    fromOtherDevice: false,
    recency: flight.createdAt,
    discardable: false,
    closing:
      flight.sessionId === null
        ? { kind: flight.isHost ? "end_flight" : "leave_flight", code: flight.code }
        : closeOf(flight.sessionId, isHost(flight.sessionId)),
    target: { kind: "flight", code: flight.code, sessionId: flight.sessionId },
  };
}

function ofSession(session: SessionSummary): UnfinishedItem {
  return {
    id: `session:${session.id}`,
    title: session.name,
    wineNo: undefined,
    wineTotal: 0,
    depth: undefined,
    startedAt: session.startedAt ?? session.scheduledAt ?? undefined,
    shared: true,
    waiting: false,
    fromOtherDevice: false,
    recency: session.startedAt ?? session.scheduledAt ?? "1970-01-01T00:00:00.000Z",
    discardable: false,
    closing: closeOf(session.id, session.isHost),
    target: { kind: "session", sessionId: session.id },
  };
}

/**
 * The list: local runs, with another device's copy standing in for a run it is newer than (and
 * shown on its own when this device has none); flights and sessions the edge names, unless a run
 * or a copy already stands for them. Newest first.
 */
export function buildUnfinished(input: UnfinishedInput): UnfinishedItem[] {
  const items = new Map<string, UnfinishedItem>();
  const heldCodes = new Set<string>();
  const heldSessions = new Set<string>();
  const note = (run: TastingRun | TastingRunSnapshot): void => {
    if (run.flightCode !== undefined) heldCodes.add(run.flightCode);
    if (run.sessionId !== undefined) heldSessions.add(run.sessionId);
  };

  for (const run of input.runs.filter(isUnfinished)) {
    items.set(`run:${run.id}`, ofRun(run));
    note(run);
  }
  for (const draft of input.drafts) {
    const local = input.runs.find((run) => run.id === draft.runId);
    const unfinishedHere = local !== undefined && isUnfinished(local);
    // A copy of a tasting that ended here is the edge catching up, not something to continue.
    if (local !== undefined && !unfinishedHere) continue;
    if (!isUnfinished(draft.snapshot)) continue;
    if (settleDraft(local, draft).winner === "local") continue;
    items.set(`run:${draft.runId}`, ofDraft(draft));
    note(draft.snapshot);
  }
  for (const flight of input.flights) {
    if (heldCodes.has(flight.code)) continue;
    items.set(`flight:${flight.code}`, ofFlight(flight, input.sessions));
    if (flight.sessionId !== null) heldSessions.add(flight.sessionId);
  }
  for (const session of input.sessions) {
    if (heldSessions.has(session.id)) continue;
    items.set(`session:${session.id}`, ofSession(session));
  }
  return [...items.values()].toSorted((a, b) => b.recency.localeCompare(a.recency));
}
